import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import Header from "@/components/Header";

// ── Cinematic background config ───────────────────────────────────────────────
// Regular person avatars only — no game/superhero character images
const ALL_BG_AVATARS = [
  "/24-girl.png", "/25-girl.png", "/26-girl.png", "/34-girl.png", "/35-girl.png",
  "/36-girl.png", "/38-girl.png", "/27-boy.png",  "/28-boy.png",  "/29-boy.png",
  "/30-boy.png",  "/31-boy.png",  "/32-boy.png",  "/33-boy.png",  "/37-boy.png",
];
const BG_COLS = [
  { dir: "up",   speed: 22, delay: "0s",   opacity: 0.80, blur: 0, gs: 55, bri: 0.60 },
  { dir: "down", speed: 29, delay: "2.1s", opacity: 0.70, blur: 0, gs: 50, bri: 0.55 },
  { dir: "up",   speed: 17, delay: "0.5s", opacity: 0.55, blur: 0, gs: 48, bri: 0.48 },
  { dir: "down", speed: 33, delay: "1.1s", opacity: 0.38, blur: 1, gs: 42, bri: 0.42 },
  { dir: "up",   speed: 25, delay: "0s",   opacity: 0.26, blur: 3, gs: 38, bri: 0.36 },
  { dir: "down", speed: 20, delay: "1.6s", opacity: 0.26, blur: 3, gs: 38, bri: 0.36 },
  { dir: "up",   speed: 31, delay: "0.3s", opacity: 0.38, blur: 1, gs: 42, bri: 0.42 },
  { dir: "down", speed: 18, delay: "2.6s", opacity: 0.55, blur: 0, gs: 48, bri: 0.48 },
  { dir: "up",   speed: 26, delay: "0.8s", opacity: 0.70, blur: 0, gs: 50, bri: 0.55 },
  { dir: "down", speed: 23, delay: "1.3s", opacity: 0.80, blur: 0, gs: 55, bri: 0.60 },
];
function getBgCol(ci) {
  const imgs = [];
  for (let i = 0; i < 4; i++) imgs.push(ALL_BG_AVATARS[(ci * 3 + i) % ALL_BG_AVATARS.length]);
  return [...imgs, ...imgs];
}

// ── Position card colours / icons ─────────────────────────────────────────────
const ROLE_META = {
  "sap-cap-java":      { icon: "☕", from: "#e63946", to: "#c1121f" },
  "sap-devops-expert": { icon: "⚙️", from: "#e63946", to: "#c1121f" },
  "general-practice":  { icon: "🎯", from: "#e63946", to: "#c1121f" },
};

function PositionCard({ position, isLoggedIn, onStart, onLoginToStart }) {
  const [busy, setBusy] = useState(false);
  const meta = ROLE_META[position.id] || { icon: "🎯", from: "#e63946", to: "#c1121f" };

  async function handleStart() {
    if (!isLoggedIn) { onLoginToStart(); return; }
    setBusy(true);
    try { await onStart(position.id); } finally { setBusy(false); }
  }

  return (
    <div
      className="rounded-xl border border-white/10 p-6 md:p-8 flex flex-col gap-5 relative overflow-hidden transition-all duration-200"
      style={{
        background: "rgba(6,10,31,0.75)",
        backdropFilter: "blur(18px)", WebkitBackdropFilter: "blur(18px)",
        boxShadow: "0 4px 30px rgba(0,0,0,0.4)",
      }}
      onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-4px)"; e.currentTarget.style.boxShadow = `0 8px 40px rgba(0,0,0,0.5), 0 0 30px ${meta.from}22`; }}
      onMouseLeave={(e) => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "0 4px 30px rgba(0,0,0,0.4)"; }}
    >
      {/* Gradient top bar */}
      <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 3, background: `linear-gradient(90deg, ${meta.from}, ${meta.to})` }} />
      {/* Corner glow */}
      <div style={{ position: "absolute", top: -40, right: -40, width: 200, height: 200, borderRadius: "50%", background: `radial-gradient(circle, ${meta.from}22 0%, transparent 70%)`, pointerEvents: "none" }} />

      {/* Use flex column so button stays at bottom regardless of description length */}
      <div style={{ position: "relative", zIndex: 1, display: "flex", flexDirection: "column", flex: 1 }}>
        {/* Icon + badge row */}
        <div className="flex items-center gap-3 mb-3">
          <span style={{ fontSize: 32 }}>{meta.icon}</span>
          <span style={{
            background: "rgba(230,57,70,0.12)", border: "1px solid rgba(230,57,70,0.35)",
            color: "#e63946", borderRadius: 20, padding: "3px 12px",
            fontSize: 11, fontWeight: 700, letterSpacing: 0.5,
          }}>{position.experience}</span>
        </div>

        <h3 className="font-display text-2xl uppercase text-white tracking-tight mb-2">{position.title}</h3>

        <p style={{ color: "rgba(255,255,255,0.5)", fontSize: 14, lineHeight: 1.7, marginBottom: "1.25rem", flex: 1 }}>
          {position.short_description}
        </p>

        {/* Tags */}
        <div className="flex flex-wrap gap-2 mb-5">
          {["AI Interviewer", "Voice + Chat", "Coding Round"].map((tag) => (
            <span key={tag} className="font-mono-kl text-[9px] uppercase tracking-widest px-2.5 py-1 rounded border"
              style={{ background: "rgba(255,255,255,0.04)", borderColor: "rgba(255,255,255,0.1)", color: "rgba(255,255,255,0.5)" }}>
              {tag}
            </span>
          ))}
        </div>

        {/* CTA — always at same vertical position */}
        <button
          onClick={handleStart}
          disabled={busy}
          className="btn-submit w-full justify-center disabled:opacity-40"
          style={isLoggedIn ? {} : {
            background: "rgba(255,255,255,0.06)",
            border: "1px solid rgba(255,255,255,0.15)",
            color: "rgba(255,255,255,0.7)",
            boxShadow: "none",
          }}
        >
          <span style={{ whiteSpace: "nowrap" }}>
            {busy ? "Starting…" : isLoggedIn ? "Start Mock Interview →" : "🔐 Login to Start →"}
          </span>
        </button>

        <p className="font-mono-kl text-[9px] uppercase tracking-widest text-white/20 text-center mt-3">
          30 min lock · max 60 min · recorded
        </p>
      </div>
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────
export default function MockPositions() {
  const navigate   = useNavigate();
  const [positions, setPositions] = useState([]);
  const [loading,   setLoading]   = useState(true);

  const token      = localStorage.getItem("mock_token");
  const isLoggedIn = !!token;
  const name       = localStorage.getItem("mock_name") || "";

  useEffect(() => {
    const fetcher = isLoggedIn ? api.mockPositions() : api.mockPublicPositions();
    fetcher
      .then((d) => setPositions(d.positions || []))
      .catch(() => toast.error("Failed to load positions"))
      .finally(() => setLoading(false));
  }, [isLoggedIn]);

  async function handleStart(positionId) {
    try {
      const session = await api.mockSessionStart(positionId);
      navigate(`/mock/session/${session.session_id}`);
    } catch (err) {
      const detail = err?.response?.data?.detail || "";
      if (detail.startsWith("active_session:")) {
        const sessionId = detail.split(":")[1];
        toast.custom((t) => (
          <div style={{
            background: "linear-gradient(135deg, #0a0f28 0%, #12193d 100%)",
            border: "1px solid rgba(230,57,70,0.5)",
            borderRadius: "14px",
            padding: "18px 20px",
            boxShadow: "0 8px 40px rgba(0,0,0,0.7), 0 0 20px rgba(230,57,70,0.15)",
            minWidth: "320px",
            maxWidth: "400px",
            color: "#fff",
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "10px" }}>
              <span style={{ fontSize: "20px" }}>⚠️</span>
              <span style={{ fontWeight: 700, fontSize: "15px", letterSpacing: "0.3px" }}>
                Active Session Found
              </span>
            </div>
            <p style={{ fontSize: "13px", color: "rgba(255,255,255,0.7)", marginBottom: "16px", lineHeight: 1.5 }}>
              You already have an interview session in progress. Resume where you left off or end it to start a new one.
            </p>
            <div style={{ display: "flex", gap: "10px" }}>
              <button
                onClick={() => { toast.dismiss(t); navigate(`/mock/session/${sessionId}`); }}
                style={{
                  flex: 1, padding: "9px 0", borderRadius: "8px", border: "none", cursor: "pointer",
                  background: "linear-gradient(90deg, #e63946, #c1121f)",
                  color: "#fff", fontWeight: 700, fontSize: "13px", letterSpacing: "0.5px",
                }}
              >
                ▶ Resume Session
              </button>
              <button
                onClick={async () => {
                  toast.dismiss(t);
                  try {
                    await api.mockSessionEnd(sessionId);
                    toast.success("Session ended. You can now start a new interview.");
                  } catch { toast.error("Could not end session. Please try again."); }
                }}
                style={{
                  flex: 1, padding: "9px 0", borderRadius: "8px", cursor: "pointer",
                  background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.15)",
                  color: "rgba(255,255,255,0.8)", fontWeight: 600, fontSize: "13px",
                }}
              >
                ✕ End Session
              </button>
            </div>
          </div>
        ), { duration: 12000 });
      } else {
        toast.error(detail || "Failed to start session");
      }
    }
  }

  function handleLoginToStart() { navigate("/mock?returnTo=positions"); }

  function handleLogout() {
    localStorage.removeItem("mock_token");
    localStorage.removeItem("mock_email");
    localStorage.removeItem("mock_name");
    navigate("/mock/positions", { replace: true });
  }

  return (
    <div className="min-h-screen text-white relative overflow-hidden" style={{ background: "var(--kl-navy-deep)" }}>
      <Header />

      {/* ── Cinematic keyframes ── */}
      <style>{`
        @keyframes mp2-up    { 0%{transform:translateY(0)}     100%{transform:translateY(-50%)} }
        @keyframes mp2-down  { 0%{transform:translateY(-50%)}  100%{transform:translateY(0)} }
        @keyframes mp2-ambi  { 0%,100%{opacity:0.10} 50%{opacity:0.26} }
        @keyframes mp2-ambi2 { 0%,100%{opacity:0.06} 50%{opacity:0.18} }
        @keyframes mp2-blink { 0%,100%{opacity:1} 50%{opacity:0.15} }
      `}</style>

      {/* ── Wall of Fighters background ── */}
      <div style={{ position: "fixed", inset: 0, zIndex: 0, overflow: "hidden", pointerEvents: "none" }}>
        {BG_COLS.map((col, ci) => (
          <div key={ci} style={{
            position: "absolute", left: `${ci * 10}%`, top: 0, bottom: 0, width: "10.4%",
            overflow: "hidden", opacity: col.opacity,
            filter: col.blur > 0 ? `blur(${col.blur}px)` : undefined,
          }}>
            <div style={{
              display: "flex", flexDirection: "column", gap: 6,
              animation: `${col.dir === "up" ? "mp2-up" : "mp2-down"} ${col.speed}s linear ${col.delay} infinite`,
            }}>
              {getBgCol(ci).map((src, ii) => (
                <img key={ii} src={src} alt="" style={{
                  width: "100%", height: "27vh", objectFit: "cover", objectPosition: "top center",
                  borderRadius: 8, flexShrink: 0,
                  filter: `grayscale(${col.gs}%) brightness(${col.bri}) contrast(1.2)`,
                }} />
              ))}
            </div>
          </div>
        ))}
        {/* Fades */}
        <div style={{ position: "absolute", inset: 0, zIndex: 1, background: "linear-gradient(90deg, rgba(6,10,31,0.88) 0%, transparent 16%, transparent 84%, rgba(6,10,31,0.88) 100%)" }} />
        <div style={{ position: "absolute", inset: 0, zIndex: 1, background: "linear-gradient(180deg, rgba(6,10,31,0.90) 0%, transparent 14%, transparent 86%, rgba(6,10,31,0.90) 100%)" }} />
        <div style={{ position: "absolute", inset: 0, zIndex: 2, background: "radial-gradient(ellipse 60% 80% at 50% 48%, rgba(6,10,31,0.88) 0%, rgba(6,10,31,0.60) 52%, transparent 100%)" }} />
        {/* Red glows */}
        <div style={{ position: "absolute", top: "-10%", left: "50%", transform: "translateX(-50%)", width: 800, height: 500, borderRadius: "50%", background: "radial-gradient(circle, rgba(230,57,70,0.14) 0%, transparent 65%)", animation: "mp2-ambi 3.5s ease-in-out infinite", zIndex: 3 }} />
        <div style={{ position: "absolute", bottom: "-10%", left: "50%", transform: "translateX(-50%)", width: 700, height: 400, borderRadius: "50%", background: "radial-gradient(circle, rgba(230,57,70,0.10) 0%, transparent 65%)", animation: "mp2-ambi2 4.5s ease-in-out 1s infinite", zIndex: 3 }} />
        {/* Cyber grid */}
        <div style={{ position: "absolute", inset: 0, opacity: 0.035, zIndex: 2, backgroundImage: "linear-gradient(rgba(230,57,70,0.9) 1px, transparent 1px), linear-gradient(90deg, rgba(230,57,70,0.9) 1px, transparent 1px)", backgroundSize: "55px 55px" }} />
      </div>

      {/* ── Content ── */}
      <div className="relative z-10 px-5 md:px-10 pt-6 pb-16">
        <div className="max-w-6xl mx-auto">

          {/* Page label + hero */}
          <div className="font-mono-kl text-xs uppercase tracking-[0.3em] text-[color:var(--kl-red)] mb-2">
            <span className="kl-accent-line mr-3" />Proctorly AI · Open Positions
          </div>
          <div className="flex flex-wrap items-end justify-between gap-4 mb-8 md:mb-10">
            <h1 className="font-display text-4xl md:text-5xl lg:text-6xl uppercase leading-[0.9] tracking-tight">
              Open <span className="text-[color:var(--kl-red)]">Positions.</span>
            </h1>

            {/* Auth controls */}
            <div className="flex items-center gap-3">
              {isLoggedIn ? (
                <>
                  {name && (
                    <span className="font-mono-kl text-[10px] uppercase tracking-widest text-white/40">
                      👤 {name}
                    </span>
                  )}
                  <button onClick={() => navigate("/mock/history")}
                    className="btn-ghost-dark text-xs">History</button>
                  <button onClick={handleLogout}
                    className="btn-ghost-dark text-xs">Sign out</button>
                </>
              ) : (
                <button onClick={() => navigate("/mock")} className="btn-submit text-sm">
                  Login / Register →
                </button>
              )}
            </div>
          </div>

          {/* Subtitle */}
          <p className="text-white/45 text-sm mb-8 max-w-lg leading-relaxed">
            Practice with a real-time AI interviewer for specific job openings.
            Get KPI-based scores and actionable feedback after every session.
          </p>

          {/* Position cards grid */}
          {loading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
              {[1, 2, 3].map((i) => (
                <div key={i} className="rounded-xl border border-white/10 h-72"
                  style={{ background: "rgba(6,10,31,0.75)", animation: "mp2-ambi 2s ease-in-out infinite" }} />
              ))}
            </div>
          ) : positions.length === 0 ? (
            <div className="rounded-xl border border-white/10 p-12 text-center"
              style={{ background: "rgba(6,10,31,0.75)", backdropFilter: "blur(18px)" }}>
              <div className="text-4xl mb-4">🎯</div>
              <div className="font-display text-2xl uppercase text-white mb-2">No open positions</div>
              <p className="text-white/40 text-sm">Check back soon — new roles are added regularly.</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
              {positions.map((pos) => (
                <PositionCard
                  key={pos.id}
                  position={pos}
                  isLoggedIn={isLoggedIn}
                  onStart={handleStart}
                  onLoginToStart={handleLoginToStart}
                />
              ))}
            </div>
          )}

          {/* Footer note */}
          <div className="mt-12 text-center text-white/20 font-mono-kl text-[10px] uppercase tracking-widest">
            Sessions are recorded for Kiba Labs admin review only · Proctorly AI · Powered by Kiba Labs
          </div>
        </div>
      </div>
    </div>
  );
}
